<template>
  <div :class="`notification-item notification-item--${type}`">
    <div class="notification-container">
      <component :is="getIcon" class="notification-icon" />
      <CloseButton @click="handleRemove" />
      <div class="notification-title">{{ title }}</div>
      <MarkupMessage class="notification-description" :text="message" />
    </div>
    <div
      v-if="duration > 0"
      class="notification-progress-line"
      :style="{ '--progress-width': progressWidth }"
    ></div>
  </div>
</template>

<script>
import clsx from 'clsx';
import CloseButton from '../close-button';
import { ErrorIcon, InfoIcon, SuccessIcon, WarningIcon } from '@/shared/ui/icons';
import { MarkupMessage } from '@/shared/ui/markup-message';

export default {
  props: {
    id: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value),
    },
    title: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      required: true,
    },
    icon: {
      type: Object,
      default: null,
      required: false,
    },
    duration: {
      type: Number,
      default: 5000,
      required: false,
    },
  },
  components: { CloseButton, MarkupMessage },
  data() {
    return {
      timeoutId: null,
      progressWidth: '100%',
      animationFrameId: null,
      startTime: null,
    };
  },
  computed: {
    getIcon() {
      if (this.icon) {
        return this.icon;
      }

      const icons = {
        success: SuccessIcon,
        error: ErrorIcon,
        warning: WarningIcon,
        info: InfoIcon,
      };

      return icons[this.type];
    },
  },
  mounted() {
    if (this.duration > 0) {
      this.startTime = Date.now();
      this.animateProgress();

      this.timeoutId = setTimeout(() => {
        this.handleRemove();
      }, this.duration);
    }
  },
  beforeDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  },
  methods: {
    clsx,
    handleRemove() {
      this.$emit('remove', this.id);
    },
    animateProgress() {
      const now = Date.now();
      const elapsed = now - this.startTime;
      const remainingPercentage = 100 - (elapsed / this.duration) * 100;

      if (remainingPercentage > 0) {
        this.progressWidth = `${Math.max(0, remainingPercentage)}%`;
        this.animationFrameId = requestAnimationFrame(this.animateProgress);
      } else {
        this.progressWidth = '0%';
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notification {
  &-item {
    font-size: 14px;
    line-height: 14px;
    border: 1px solid #e4e4e7;
    border-radius: 4px;
    text-align: left;
    background-color: #ffffff;
    box-shadow: 0px 1px 2px 0px #0000000f, 0px 1px 3px 0px #0000001a;
    animation: notification-in-right 0.7s ease-in;
    overflow: hidden;
    position: relative;
    margin-bottom: 10px;
    z-index: 5000;
  }

  p {
    margin-bottom: 0;
  }

  &-container {
    position: relative;
    padding: 14px 65px;
  }

  &-description {
    color: #020617;
    line-height: 20px;
  }

  &-title {
    margin-bottom: 5px;
    font-weight: 600;
    line-height: 1.3;
  }

  &-icon {
    display: inline-block;
    width: 50px;
    height: 50px;
    position: absolute;
    top: 50%;
    left: 10px;
    transform: translateY(-50%);
    background-repeat: no-repeat;
    background-position: center center;
  }

  &-progress-line {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    background-color: #FB923C;
    width: var(--progress-width, 100%);
    transition: width 0.1s linear;
    z-index: 1;
  }
}

@keyframes notification-in-right {
  from {
    transform: translateX(120%);
    opacity: 0;
  }
  to {
    transform: translateX(0%);
    opacity: 1;
  }
}
</style>
